import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { AddressBar, Marquee, Mast, StatusBar, WEB, xpCss, webCss } from 'lib/xp';
import { fetchMsft, fmtUsd, fmtCompact, short, MSFT, EXPLORER } from 'data/msft';
import { useLedger } from 'data/ledger';

import pfp from 'assets/pfp.png';

// MSFT.exe — the stock the internet is quoted in. Live, chain-derived numbers
// from DexScreener (deepest MSFT/stable pool on Robinhood Chain 4663). This is
// the proof-of-meta window: the pair asset is real, trades 24/7, and every
// $INTERNET trade fee routes to holders AS this asset. No projections, ever.
//
// Restyled 2026-09-13 onto lib/xp's `web` layer (sky — a brighter Microsoft blue,
// fitting for Microsoft's own quote window). The beige pass this replaces was, in
// Kris's words, "gray, boring, hard to read"; the readability rule it also broke
// is dark ink on light, light ink on dark, never ink on ink. Lime now survives in
// exactly two places: the marquee and the price's LED plate.

const REFRESH_MS = 45000;

export default function Msft() {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const { paidTotal: paidToHolders, epochs } = useLedger();

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetchMsft().then(x => {
        if (!alive) return;
        setD(x);
        setLoading(false);
      });
    load();
    const t = setInterval(load, REFRESH_MS);
    const c = setInterval(() => setTick(n => n + 1), 1000);
    return () => {
      alive = false;
      clearInterval(t);
      clearInterval(c);
    };
  }, []);

  const age = d ? Math.max(0, Math.round((Date.now() - d.at) / 1000)) : 0;
  const up = d && typeof d.chg24 === 'number' ? d.chg24 >= 0 : true;
  const tape = d
    ? `MSFT ${up ? '▲' : '▼'} ${fmtUsd(d.price)}   ·   24H VOL ${fmtCompact(d.vol24)}   ·   LIQUIDITY ${fmtCompact(d.liq)}   ·   MSFT PAID TO HOLDERS ${paidToHolders.toFixed(2)}   ·   OPEN 24/7   ·   PAYS AROUND THE CLOCK   ·   `
    : 'MSFT   ·   NO SIGNAL   ·   THE TAPE RESUMES WHEN THE FEED ANSWERS   ·   OPEN 24/7   ·   ';

  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">File</button>
        <button type="button">View</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">Microsoft • Robinhood Token</span>
      </div>
      <AddressBar path="msft.internetcoin.vip/quote.htm" />

      <div className="xp__client web web--sky">
        <Mast
          logo={pfp}
          kicker="the stock the internet is quoted in"
          title="MSFT.exe"
          right={
            <>
              <span className="web__sticker web__sticker--gold">OPEN 24/7</span>
              <span className={`web__sticker${d && !d.stale ? ' web__sticker--blink' : ' web__sticker--blue'}`}>
                {d && !d.stale ? 'LIVE' : 'FEED'}
              </span>
            </>
          }
        />
        <Marquee>{tape.repeat(2)}</Marquee>

        <div className="web__body">
          <div className="web__cols">
            <div className="web__panel web__panel--sky ms__quote">
              <div className="web__panel__t">Quote</div>
              <div className="ms__name">MICROSOFT • ROBINHOOD TOKEN</div>
              <div className="ms__sub">tokenized MSFT · Robinhood Chain 4663 · the stock the internet is quoted in</div>
              <span className={`web__led web__led--big ms__price ${d ? (up ? 'up' : 'down') : ''}`}>
                {loading ? 'loading…' : d ? fmtUsd(d.price) : 'NO SIGNAL'}
              </span>
              {d && typeof d.chg24 === 'number' && (
                <div className={`ms__chg ${up ? 'is-up' : 'is-down'}`}>
                  {up ? '+' : ''}
                  {d.chg24.toFixed(2)}% 24h
                </div>
              )}
            </div>

            <div className="web__panel web__panel--navy ms__market">
              <div className="web__panel__t">Market data</div>
              <table className="web__table ms__table">
                <tbody>
                  <Row k="24h volume" v={d ? fmtCompact(d.vol24) : '——'} />
                  <Row k="pool liquidity" v={d ? fmtCompact(d.liq) : '——'} />
                  <Row k="deepest pool" v={d ? `MSFT / ${d.quote} · ${d.dex}` : '——'} />
                  <Row
                    k="MSFT paid to holders"
                    v={`${paidToHolders.toFixed(4)} MSFT · ${
                      paidToHolders === 0 ? 'first epoch after launch' : `${epochs.length} epoch${epochs.length === 1 ? '' : 's'} · live · see DIVIDENDS.exe`
                    }`}
                    hl
                  />
                  <Row
                    k="asset address"
                    v={
                      <a href={`${EXPLORER}/token/${MSFT}`} target="_blank" rel="noreferrer noopener">
                        {short(MSFT)} ↗
                      </a>
                    }
                  />
                  <Row k="market hours" v="00:00 – 24:00, every day" hl />
                </tbody>
              </table>
            </div>
          </div>

          <div className="web__panel web__panel--white ms__why">
            <div className="web__panel__t">Why this window exists</div>
            <p>
              $INTERNET launches on pons V2 <b>paired with this asset</b>. Traders pay 2% per trade
              (1% creator tax + pons&apos; 1%), and 1.7% of every trade is airdropped to holders as tokenized
              MSFT — it never has to be swapped, it simply arrives as the pair. Wall Street
              closes at 4pm. This doesn&apos;t.
            </p>
            <p className="ms__fine">
              Live market data via DexScreener · MSFT here is a third-party tokenized product, not Microsoft
              stock · parody — not affiliated with, endorsed by, or sponsored by Microsoft Corporation ·
              nothing here is a promise or a projection.
            </p>
          </div>

          <div className="web__hr" />
          <div className="web__foot">Live market data via DexScreener · best viewed at 800×600 · © 2001 Internet Coin</div>
        </div>
      </div>

      <StatusBar>
        <span>
          {d
            ? `${d.stale ? 'last good reading' : 'live'} · updated ${age}s ago`
            : 'feed unavailable — showing NO SIGNAL, not a fake number'}
        </span>
        <span>
          {d && (
            <a href={d.pairUrl} target="_blank" rel="noreferrer noopener">
              open chart ↗
            </a>
          )}
        </span>
        <span>Internet</span>
      </StatusBar>
    </Div>
  );
}

function Row({ k, v, hl }) {
  return (
    <tr className={hl ? 'ms__row--hl' : ''}>
      <th scope="row" className="ms__k">
        {k}
      </th>
      <td className="ms__v">{v}</td>
    </tr>
  );
}

const Div = styled.div`
  /* a 440 px window: one column, always; and values that wrap rather than clip */
  .web__cols {
    flex-direction: column;
  }
  .web__table th {
    width: 38%;
    vertical-align: top;
  }
  .web__table td {
    word-break: break-word;
  }
  .web__led--big.ms__price {
    font-size: 22px;
  }
  ${xpCss};
  ${webCss};
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 0;
  }

  /* the quote panel: name + sub above the price plate, the change line below it */
  .ms__name {
    font-size: 14px;
    font-weight: bold;
    color: ${WEB.ink};
    margin-bottom: 3px;
  }
  .ms__sub {
    font-size: 10.5px;
    color: ${WEB.ink};
    line-height: 1.4;
    margin-bottom: 10px;
  }
  .ms__price.up {
    color: ${WEB.lime};
  }
  .ms__price.down {
    color: #fff;
  }
  .ms__chg {
    margin-top: 8px;
    font-size: 12px;
    font-weight: bold;
  }
  .ms__chg.is-up {
    color: #1e8b3a;
  }
  .ms__chg.is-down {
    color: ${WEB.red};
  }

  /* market data: row-header key, plain value; hl rows bold the value only */
  .ms__table th {
    white-space: nowrap;
  }
  .ms__row--hl td {
    font-weight: bold;
  }

  .ms__fine {
    font-size: 10px;
    color: ${WEB.goldInk};
  }

  
`;
